import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from './api';

// ── Constants ─────────────────────────────────────────────────────────────────
const SVG_W = 390;
const SVG_H = 520;
const DOT_R = 7;          // 1/3 of original 22
const ZOOM_MIN = 1.0;
const ZOOM_MAX = 5.0;
const ZOOM_STEP = 0.25;
const ZOOM_DEFAULT = 1.9;
const SCROLL_STEP = 90;

// ── Helpers ───────────────────────────────────────────────────────────────────
function toKey(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function dist2D(a, b) {
  return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
}

function svgCoords(e, svgEl) {
  const rect = svgEl.getBoundingClientRect();
  return {
    x: ((e.clientX - rect.left) / rect.width)  * SVG_W,
    y: ((e.clientY - rect.top)  / rect.height) * SVG_H,
  };
}

// Perpendicular unit-vector scaled by d — used for triple-line offset
function perpVec(x1, y1, x2, y2, d) {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  return { px: (-dy / len) * d, py: (dx / len) * d };
}

// ── Required parts (from PART_TO_STYPE in CIV) — user must configure these ────
const REQUIRED_PARTS = [
  // Front — Torso / Organs
  { key: 'cervical',     label: 'Neck',               stype: 'Cervical (Neck)',    view: 'front' },
  { key: 'chest',        label: 'Chest',               stype: 'Chest',              view: 'front' },
  { key: 'heart',        label: 'Heart',               stype: 'Heart',              view: 'front' },
  { key: 'lung',         label: 'Lungs',               stype: 'Lungs',              view: 'front' },
  { key: 'digestive',    label: 'Digestive',           stype: 'Digestive',          view: 'front' },
  { key: 'abdomen',      label: 'Abdomen',             stype: 'Abdomen',            view: 'front' },
  { key: 'renal',        label: 'Renal',               stype: 'Renal',              view: 'front' },
  { key: 'reproductive', label: 'Pelvis',              stype: 'Reproductive',       view: 'front' },
  // Front — Arms L
  { key: 'shoulder_L',   label: 'L Shoulder',          stype: 'Shoulder',           view: 'front' },
  { key: 'arm_L',        label: 'L Upper Arm',         stype: 'Arm',                view: 'front' },
  { key: 'elbow_L',      label: 'L Elbow',             stype: 'Elbow',              view: 'front' },
  { key: 'wrist_L',      label: 'L Wrist',             stype: 'Wrist',              view: 'front' },
  { key: 'palm_L',       label: 'L Palm',              stype: 'Hand',               view: 'front' },
  { key: 'fingers_L',    label: 'L Fingers',           stype: 'Hand',               view: 'front' },
  // Front — Arms R
  { key: 'shoulder_R',   label: 'R Shoulder',          stype: 'Shoulder',           view: 'front' },
  { key: 'arm_R',        label: 'R Upper Arm',         stype: 'Arm',                view: 'front' },
  { key: 'elbow_R',      label: 'R Elbow',             stype: 'Elbow',              view: 'front' },
  { key: 'wrist_R',      label: 'R Wrist',             stype: 'Wrist',              view: 'front' },
  { key: 'palm_R',       label: 'R Palm',              stype: 'Hand',               view: 'front' },
  { key: 'fingers_R',    label: 'R Fingers',           stype: 'Hand',               view: 'front' },
  // Front — Legs L
  { key: 'hip_L',        label: 'L Hip',               stype: 'Hip',                view: 'front' },
  { key: 'thigh_L',      label: 'L Thigh',             stype: 'Thigh',              view: 'front' },
  { key: 'knee_L',       label: 'L Knee',              stype: 'Knee',               view: 'front' },
  { key: 'calf_L',       label: 'L Shin/Calf',         stype: 'Calf',               view: 'front' },
  { key: 'ankle_L',      label: 'L Ankle',             stype: 'Ankle',              view: 'front' },
  { key: 'foot_L',       label: 'L Foot',              stype: 'Foot',               view: 'front' },
  { key: 'toes_L',       label: 'L Toes',              stype: 'Foot',               view: 'front' },
  // Front — Legs R
  { key: 'hip_R',        label: 'R Hip',               stype: 'Hip',                view: 'front' },
  { key: 'thigh_R',      label: 'R Thigh',             stype: 'Thigh',              view: 'front' },
  { key: 'knee_R',       label: 'R Knee',              stype: 'Knee',               view: 'front' },
  { key: 'calf_R',       label: 'R Shin/Calf',         stype: 'Calf',               view: 'front' },
  { key: 'ankle_R',      label: 'R Ankle',             stype: 'Ankle',              view: 'front' },
  { key: 'foot_R',       label: 'R Foot',              stype: 'Foot',               view: 'front' },
  { key: 'toes_R',       label: 'R Toes',              stype: 'Foot',               view: 'front' },
  // Back — Spine
  { key: 'neckback',     label: 'Neck (Back)',          stype: 'Cervical (Neck)',    view: 'back'  },
  { key: 'upperback',    label: 'Upper Back',           stype: 'UpperBack',          view: 'back'  },
  { key: 'middleback',   label: 'Mid Back',             stype: 'MiddleBack',         view: 'back'  },
  { key: 'lowerback',    label: 'Lower Back',           stype: 'LowerBack & Pelvis', view: 'back'  },
  { key: 'coccyx',       label: 'Coccyx/Sacrum',        stype: 'Coccyx',             view: 'back'  },
  // Back — Arms L
  { key: 'shoulder_LB',  label: 'L Shoulder (Back)',    stype: 'Shoulder',           view: 'back'  },
  { key: 'arm_LB',       label: 'L Upper Arm (Back)',   stype: 'Arm',                view: 'back'  },
  { key: 'elbow_LB',     label: 'L Elbow (Back)',       stype: 'Elbow',              view: 'back'  },
  // Back — Arms R
  { key: 'shoulder_RB',  label: 'R Shoulder (Back)',    stype: 'Shoulder',           view: 'back'  },
  { key: 'arm_RB',       label: 'R Upper Arm (Back)',   stype: 'Arm',                view: 'back'  },
  { key: 'elbow_RB',     label: 'R Elbow (Back)',       stype: 'Elbow',              view: 'back'  },
  // Back — Legs L
  { key: 'hip_LB',       label: 'L Hip (Back)',         stype: 'Hip',                view: 'back'  },
  { key: 'hamstring_L',  label: 'L Hamstring',          stype: 'Leg',                view: 'back'  },
  { key: 'knee_LB',      label: 'L Knee (Back)',        stype: 'Knee',               view: 'back'  },
  { key: 'calf_LB',      label: 'L Calf (Back)',        stype: 'Calf',               view: 'back'  },
  { key: 'ankle_LB',     label: 'L Achilles',           stype: 'Ankle',              view: 'back'  },
  { key: 'heel_L',       label: 'L Heel',               stype: 'Foot',               view: 'back'  },
  // Back — Legs R
  { key: 'hip_RB',       label: 'R Hip (Back)',         stype: 'Hip',                view: 'back'  },
  { key: 'hamstring_R',  label: 'R Hamstring',          stype: 'Leg',                view: 'back'  },
  { key: 'knee_RB',      label: 'R Knee (Back)',        stype: 'Knee',               view: 'back'  },
  { key: 'calf_RB',      label: 'R Calf (Back)',        stype: 'Calf',               view: 'back'  },
  { key: 'ankle_RB',     label: 'R Achilles (Back)',    stype: 'Ankle',              view: 'back'  },
  { key: 'heel_R',       label: 'R Heel',               stype: 'Foot',               view: 'back'  },
];

// Unique sorted stype options derived from REQUIRED_PARTS
const STYPE_OPTIONS = [...new Set(REQUIRED_PARTS.map(p => p.stype))].filter(Boolean).sort();

// ── Sub-components ────────────────────────────────────────────────────────────
function LineAnnot({ a, showLabel, selected }) {
  const isTriple = a.lineStyle === 'triple';
  const { px, py } = isTriple ? perpVec(a.x1, a.y1, a.x2, a.y2, 5) : { px: 0, py: 0 };
  const midX = (a.x1 + a.x2) / 2;
  const midY = (a.y1 + a.y2) / 2;
  const lineColor = selected ? '#4f46e5' : '#ef4444';
  const dotColor  = selected ? '#4338ca' : '#dc2626';
  return (
    <>
      {/* Invisible fat hit-area */}
      <line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke="transparent" strokeWidth={12} />
      {/* Visible strokes */}
      <line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke={lineColor} strokeWidth={selected ? 3.5 : 2.5} strokeLinecap="round" />
      {isTriple && (
        <>
          <line x1={a.x1 + px} y1={a.y1 + py} x2={a.x2 + px} y2={a.y2 + py} stroke={lineColor} strokeWidth="1.5" strokeLinecap="round" />
          <line x1={a.x1 - px} y1={a.y1 - py} x2={a.x2 - px} y2={a.y2 - py} stroke={lineColor} strokeWidth="1.5" strokeLinecap="round" />
        </>
      )}
      {/* Endpoint dots */}
      <circle cx={a.x1} cy={a.y1} r={3} fill={dotColor} />
      <circle cx={a.x2} cy={a.y2} r={3} fill={dotColor} />
      {/* Label — on hover or selected */}
      {showLabel && (
        <text x={midX} y={midY - 8} textAnchor="middle"
          fontSize="9" fontWeight="700" fill={selected ? '#4f46e5' : '#1e293b'}
          stroke="#fff" strokeWidth="3" paintOrder="stroke"
          style={{ pointerEvents: 'none' }}>
          {a.label}
        </text>
      )}
    </>
  );
}

function PendingLinePreview({ p, lineStyle }) {
  const isTriple = lineStyle === 'triple';
  const { px, py } = isTriple ? perpVec(p.x1, p.y1, p.x2, p.y2, 5) : { px: 0, py: 0 };
  return (
    <g style={{ pointerEvents: 'none' }}>
      <line x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" />
      {isTriple && (
        <>
          <line x1={p.x1 + px} y1={p.y1 + py} x2={p.x2 + px} y2={p.y2 + py} stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" />
          <line x1={p.x1 - px} y1={p.y1 - py} x2={p.x2 - px} y2={p.y2 - py} stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" />
        </>
      )}
      <circle cx={p.x1} cy={p.y1} r={3} fill="#f59e0b" />
      <circle cx={p.x2} cy={p.y2} r={3} fill="#f59e0b" />
    </g>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const btnBase = {
  border: 'none', borderRadius: 7, padding: '5px 12px',
  fontWeight: 700, fontSize: 12, cursor: 'pointer',
};
const iconBtn = {
  border: 'none', borderRadius: 6, background: '#1e293b',
  color: '#94a3b8', padding: '4px 9px', fontSize: 15, cursor: 'pointer', fontWeight: 700,
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function BPSelection() {
  const [view,        setView]       = useState('front');
  const [annotations, setAnnotations] = useState({ front: [], back: [] });

  // Pending annotation: dot → {type:'dot', cx,cy,r, isvgX,isvgY}
  //                     line → {type:'line', x1,y1,x2,y2, isvgX,isvgY}
  const [pending,          setPending]          = useState(null);
  const [pendingLabel,     setPendingLabel]     = useState('');
  const [pendingStype,     setPendingStype]     = useState('');       // sequence category
  const [pendingLineStyle, setPendingLineStyle] = useState('single'); // 'single'|'triple'

  // Drag preview while pointer is still down
  const [dragPreview, setDragPreview] = useState(null); // { start:{x,y}, current:{x,y} }

  const [editId,    setEditId]    = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [hoveredId, setHoveredId] = useState(null);

  const [zoom,       setZoom]       = useState(ZOOM_DEFAULT);
  const [saving,     setSaving]     = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // '' | 'saved' | 'error'
  const [showExport, setShowExport] = useState(false);

  // Required-parts panel state
  const [rightTab,           setRightTab]           = useState('custom');   // 'custom' | 'required'
  const [placingRequiredKey, setPlacingRequiredKey] = useState(null);       // null | {key, label, view}
  const [flipPhase,          setFlipPhase]          = useState(null);       // null | 'exit' | 'enter'
  const [linkEditKey,        setLinkEditKey]        = useState(null);       // key of part whose ROM links are being edited
  const [selectedInfoPart,   setSelectedInfoPart]   = useState(null);       // annotation object shown in info box
  const [clinicalConfig,     setClinicalConfig]     = useState(null);       // ClinicalConfig (romParts + muscleParts)
  const [clinicalEditKey,    setClinicalEditKey]    = useState(null);       // key of part whose romKey/muscles are being edited

  const svgRef       = useRef(null);
  const dragRef      = useRef(null);
  const inputRef     = useRef(null);
  const editInputRef = useRef(null);
  const scrollRef    = useRef(null);

  const current = annotations[view];

  // ── View flip with space-travel animation ──────────────────────────────────
  const switchView = (newView) => {
    if (newView === view || flipPhase) return;
    setPending(null);
    setSelectedInfoPart(null);
    setFlipPhase('exit');
    setTimeout(() => {
      setView(newView);
      setFlipPhase('enter');
      setTimeout(() => setFlipPhase(null), 650);
    }, 650);
  };

  // ── Load from backend ─────────────────────────────────────────────────────
  useEffect(() => {
    api.get(`${process.env.REACT_APP_URL}/bodyparts`)
      .then(res => {
        if (res.data && (res.data.front?.length || res.data.back?.length)) {
          setAnnotations({ front: res.data.front || [], back: res.data.back || [] });
        }
      })
      .catch(() => {});
    // Load ClinicalConfig (ROM joints + muscles reference data)
    api.get(`${process.env.REACT_APP_URL}/bodyparts/clinical`)
      .then(res => { if (res.data) setClinicalConfig(res.data); })
      .catch(() => {});
  }, []);

  useEffect(() => { if (pending    && inputRef.current)     inputRef.current.focus();     }, [pending]);
  useEffect(() => { if (editId     && editInputRef.current) editInputRef.current.focus(); }, [editId]);

  // ── Pointer handlers ──────────────────────────────────────────────────────
  const onPointerDown = useCallback((e) => {
    if (pending) return;
    if (!svgRef.current) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const pt = svgCoords(e, svgRef.current);
    dragRef.current = { start: pt, current: pt };
    setDragPreview({ start: pt, current: pt });
  }, [pending]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    const pt = svgCoords(e, svgRef.current);
    dragRef.current.current = pt;
    setDragPreview({ start: dragRef.current.start, current: pt });
  }, []);

  const onPointerUp = useCallback((e) => {
    if (!dragRef.current) return;
    const start = dragRef.current.start;
    const end   = svgCoords(e, svgRef.current);
    dragRef.current = null;
    setDragPreview(null);

    const d = dist2D(start, end);

    // ── Required-part placement mode: click = instant dot, drag = ignored ──
    if (placingRequiredKey) {
      if (d < 10) {
        const { key, label, stype } = placingRequiredKey;
        // Preserve existing metricType/romLinkedParts from previous placement
        const existing = annotations.front.find(a => a.key === key) || annotations.back.find(a => a.key === key);
        const newAnnot = {
          id:    `req_${key}`,
          key,
          label,
          stype: stype || '',
          type:  'dot',
          cx:    Math.round(start.x),
          cy:    Math.round(start.y),
          r:     DOT_R,
          metricType:     existing?.metricType || '',
          romLinkedParts: existing?.romLinkedParts || [],
          romKey:         existing?.romKey || '',
          muscles:        existing?.muscles || [],
        };
        setAnnotations(prev => {
          // Replace existing dot with same key if already placed (remove from both views)
          const filtered = prev[view].filter(a => a.key !== key);
          const otherView = view === 'front' ? 'back' : 'front';
          const otherFiltered = prev[otherView].filter(a => a.key !== key);
          return { ...prev, [view]: [...filtered, newAnnot], [otherView]: otherFiltered };
        });
        setPlacingRequiredKey(null);
      }
      return; // ignore drags in required-placement mode
    }

    if (d < 10) {
      // Click → dot
      setPending({
        type: 'dot',
        cx: Math.round(start.x),
        cy: Math.round(start.y),
        r:  DOT_R,
        isvgX: start.x + DOT_R + 6,
        isvgY: start.y - 18,
      });
    } else {
      // Drag → line
      const midX = (start.x + end.x) / 2;
      const midY = (start.y + end.y) / 2;
      setPending({
        type: 'line',
        x1: Math.round(start.x), y1: Math.round(start.y),
        x2: Math.round(end.x),   y2: Math.round(end.y),
        isvgX: midX + 10,
        isvgY: midY - 22,
      });
    }
    setPendingLabel('');
  }, [placingRequiredKey, view]);

  // ── Confirm / cancel pending ──────────────────────────────────────────────
  const confirmPending = useCallback(() => {
    if (!pendingLabel.trim()) { setPending(null); return; }
    const label = pendingLabel.trim();
    const newAnnot = {
      id:    `a_${Date.now()}`,
      key:   toKey(label),
      label,
      stype: pendingStype.trim(),
      type:  pending.type,
      ...(pending.type === 'dot'
        ? { cx: pending.cx, cy: pending.cy, r: pending.r }
        : { x1: pending.x1, y1: pending.y1, x2: pending.x2, y2: pending.y2, lineStyle: pendingLineStyle }
      ),
    };
    setAnnotations(prev => ({ ...prev, [view]: [...prev[view], newAnnot] }));
    setPending(null);
    setPendingLabel('');
    setPendingStype('');
    setPendingLineStyle('single');
  }, [pending, pendingLabel, pendingStype, pendingLineStyle, view]);

  const cancelPending = useCallback(() => {
    setPending(null);
    setPendingLabel('');
    setPendingStype('');
    setPendingLineStyle('single');
  }, []);

  // ── Edit annotation label ─────────────────────────────────────────────────
  const startEdit = useCallback((id, label) => {
    setEditId(id); setEditLabel(label);
  }, []);

  const confirmEdit = useCallback(() => {
    if (!editLabel.trim()) { setEditId(null); return; }
    const label = editLabel.trim();
    setAnnotations(prev => ({
      ...prev,
      [view]: prev[view].map(a => a.id === editId ? { ...a, label, key: toKey(label) } : a),
    }));
    setEditId(null);
  }, [editId, editLabel, view]);

  const deleteAnnotation = useCallback((id) => {
    setAnnotations(prev => ({ ...prev, [view]: prev[view].filter(a => a.id !== id) }));
  }, [view]);

  // ── Required-part helpers ─────────────────────────────────────────────────
  const isConfigured = (key) =>
    annotations.front.some(a => a.key === key) || annotations.back.some(a => a.key === key);

  const configuredCount = REQUIRED_PARTS.filter(p => isConfigured(p.key)).length;

  // ── Metric type & ROM linkage helpers ─────────────────────────────────────
  const getAnnotation = (key) =>
    annotations.front.find(a => a.key === key) || annotations.back.find(a => a.key === key);

  const updateAnnotationField = (key, field, value) => {
    setAnnotations(prev => {
      const updated = { ...prev };
      ['front', 'back'].forEach(v => {
        updated[v] = prev[v].map(a => a.key === key ? { ...a, [field]: value } : a);
      });
      return updated;
    });
  };

  const toggleMetricType = (key) => {
    const ann = getAnnotation(key);
    const current = ann?.metricType || '';
    // Cycle: (none) → MVC → ROM → both → (none)
    const next = current === '' ? 'MVC' : current === 'MVC' ? 'ROM' : current === 'ROM' ? 'both' : '';
    updateAnnotationField(key, 'metricType', next);
    if (next !== 'ROM' && next !== 'both') updateAnnotationField(key, 'romLinkedParts', []);
  };

  const toggleRomLink = (partKey, linkedKey) => {
    const ann = getAnnotation(partKey);
    const links = ann?.romLinkedParts || [];
    const next = links.includes(linkedKey)
      ? links.filter(k => k !== linkedKey)
      : [...links, linkedKey];
    updateAnnotationField(partKey, 'romLinkedParts', next);
  };

  // All configured parts (required + custom) for linkage picker
  const allConfiguredParts = [
    ...REQUIRED_PARTS.filter(p => isConfigured(p.key)).map(p => ({ key: p.key, label: p.label })),
    ...annotations.front.filter(a => !REQUIRED_PARTS.some(r => r.key === a.key)).map(a => ({ key: a.key, label: a.label })),
    ...annotations.back.filter(a => !REQUIRED_PARTS.some(r => r.key === a.key) && !annotations.front.some(f => f.key === a.key)).map(a => ({ key: a.key, label: a.label })),
  ];

  // ── Save ──────────────────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true); setSaveStatus('');
    try {
      await api.post(`${process.env.REACT_APP_URL}/bodyparts`, annotations);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2500);
    } catch (err) {
      setSaveStatus('error');
      alert('Save failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ── Export JSON ───────────────────────────────────────────────────────────
  const exportJSON = () => {
    const toArr = list => list.map(a => {
      if (a.type === 'dot')
        return { key: a.key, label: a.label, cx: a.cx, cy: a.cy };
      return {
        key: a.key, label: a.label,
        cx: Math.round((a.x1 + a.x2) / 2), cy: Math.round((a.y1 + a.y2) / 2),
        x1: a.x1, y1: a.y1, x2: a.x2, y2: a.y2, lineStyle: a.lineStyle,
      };
    });
    return JSON.stringify({ front: toArr(annotations.front), back: toArr(annotations.back) }, null, 2);
  };

  // ── Zoom helpers ──────────────────────────────────────────────────────────
  const zoomIn    = () => setZoom(z => Math.min(ZOOM_MAX,  +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut   = () => setZoom(z => Math.max(ZOOM_MIN,  +(z - ZOOM_STEP).toFixed(2)));
  const zoomReset = () => setZoom(ZOOM_DEFAULT);

  // ── Scroll helpers (▲▼ buttons) ───────────────────────────────────────────
  const scrollUp   = () => scrollRef.current?.scrollBy({ top: -SCROLL_STEP, behavior: 'smooth' });
  const scrollDown = () => scrollRef.current?.scrollBy({ top:  SCROLL_STEP, behavior: 'smooth' });

  // ── Drag preview SVG element ──────────────────────────────────────────────
  let previewEl = null;
  if (dragPreview) {
    const d = dist2D(dragPreview.start, dragPreview.current);
    if (d < 10) {
      previewEl = (
        <circle cx={dragPreview.start.x} cy={dragPreview.start.y} r={DOT_R}
          fill="rgba(59,130,246,0.3)" stroke="#3b82f6" strokeWidth="1.5"
          style={{ pointerEvents: 'none' }}
        />
      );
    } else {
      previewEl = (
        <line
          x1={dragPreview.start.x} y1={dragPreview.start.y}
          x2={dragPreview.current.x} y2={dragPreview.current.y}
          stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round"
          strokeDasharray="6 3" style={{ pointerEvents: 'none' }}
        />
      );
    }
  }

  const svgPxW = SVG_W * zoom;
  const svgPxH = SVG_H * zoom;

  // Popup position in screen pixels (clamped to SVG rendered bounds)
  const popupLeft = pending ? Math.min(pending.isvgX * zoom, svgPxW - 220) : 0;
  const popupTop  = pending ? Math.max(pending.isvgY * zoom, 4) : 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: '#f1f5f9' }}>
      <style>{`
        @keyframes bpFlipExit {
          0%   { transform: perspective(1400px) rotateY(0deg) scale(1); }
          60%  { transform: perspective(1400px) rotateY(-60deg) scale(0.88); }
          100% { transform: perspective(1400px) rotateY(-90deg) scale(0.8); opacity: 0.1; }
        }
        @keyframes bpFlipEnter {
          0%   { transform: perspective(1400px) rotateY(90deg) scale(0.8); opacity: 0.1; }
          40%  { transform: perspective(1400px) rotateY(30deg) scale(0.92); }
          100% { transform: perspective(1400px) rotateY(0deg) scale(1); opacity: 1; }
        }
        @keyframes bpSpaceAura {
          0%   { opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes bpWarpStreak {
          0%   { opacity: 0; transform: scaleX(0) rotate(var(--r, 0deg)); }
          30%  { opacity: 1; transform: scaleX(1) rotate(var(--r, 0deg)); }
          70%  { opacity: 1; transform: scaleX(1.4) rotate(var(--r, 0deg)); }
          100% { opacity: 0; transform: scaleX(2.5) rotate(var(--r, 0deg)); }
        }
        @keyframes bpEnergyRing {
          0%   { transform: translate(-50%,-50%) scale(0.2); opacity: 0; }
          30%  { opacity: 1; }
          70%  { opacity: 0.7; }
          100% { transform: translate(-50%,-50%) scale(2.5); opacity: 0; }
        }
        @keyframes bpOrbPulse {
          0%   { transform: translate(-50%,-50%) scale(0.3); opacity: 0; }
          20%  { opacity: 1; }
          60%  { transform: translate(-50%,-50%) scale(1.15); }
          100% { transform: translate(-50%,-50%) scale(0.8); opacity: 0.7; }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        padding: '10px 18px', background: '#1e293b', color: '#fff', flexShrink: 0,
      }}>
        <span style={{ fontSize: 18 }}>🧍</span>
        <strong style={{ fontSize: 15 }}>Body Part Configurator</strong>
        <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>
          Click&nbsp;=&nbsp;dot&nbsp;&nbsp;·&nbsp;&nbsp;Drag&nbsp;=&nbsp;line&nbsp;&nbsp;·&nbsp;&nbsp;Double-click to rename
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={() => setShowExport(v => !v)}
            style={{ ...btnBase, background: '#334155', color: '#cbd5e1' }}>
            {showExport ? 'Hide JSON' : 'Export JSON'}
          </button>
          <button onClick={save} disabled={saving} style={{
            ...btnBase, color: '#fff', opacity: saving ? 0.7 : 1,
            background: saveStatus === 'saved' ? '#16a34a' : saveStatus === 'error' ? '#dc2626' : '#22c55e',
          }}>
            {saveStatus === 'saved' ? '✓ Saved' : saveStatus === 'error' ? '✗ Error' : saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* ── Tab bar + zoom + scroll controls ── */}
      <div style={{
        display: 'flex', alignItems: 'center',
        background: '#0f172a', flexShrink: 0,
      }}>
        {['front', 'back'].map(v => (
          <button key={v} onClick={() => switchView(v)} style={{
            padding: '9px 28px', border: 'none', cursor: flipPhase ? 'not-allowed' : 'pointer',
            fontWeight: 700, fontSize: 13, textTransform: 'capitalize',
            background: view === v ? '#3b82f6' : 'transparent',
            color:      view === v ? '#fff'    : '#94a3b8',
            transition: 'background 0.2s',
          }}>{v === 'front' ? '🧍 Front' : '🔄 Back'}</button>
        ))}

        {/* Controls on the right */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 3, paddingRight: 12 }}>
          {/* Scroll arrows */}
          <button onClick={scrollUp}   title="Scroll up"   style={iconBtn}>▲</button>
          <button onClick={scrollDown} title="Scroll down" style={iconBtn}>▼</button>

          {/* Divider */}
          <div style={{ width: 1, height: 18, background: '#334155', margin: '0 5px' }} />

          {/* Zoom controls */}
          <button onClick={zoomOut} title="Zoom out" style={{ ...iconBtn, fontSize: 18, lineHeight: 1 }}>−</button>
          <span style={{ fontSize: 12, color: '#94a3b8', minWidth: 40, textAlign: 'center', userSelect: 'none' }}>
            {Math.round(zoom * 100)}%
          </span>
          <button onClick={zoomIn}    title="Zoom in"    style={{ ...iconBtn, fontSize: 18, lineHeight: 1 }}>+</button>
          <button onClick={zoomReset} title="Reset zoom" style={{ ...iconBtn, fontSize: 11, padding: '4px 7px' }}>⊕</button>

          {/* Divider */}
          <div style={{ width: 1, height: 18, background: '#334155', margin: '0 5px' }} />
          <span style={{ fontSize: 11, color: '#475569' }}>{current.length} pts</span>
        </div>
      </div>

      {/* ── Export JSON panel ── */}
      {showExport && (
        <div style={{ background: '#0f172a', padding: '10px 18px', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
              CIV-compatible — dots use cx/cy; lines include midpoint + x1,y1,x2,y2
            </span>
            <button onClick={() => navigator.clipboard?.writeText(exportJSON())}
              style={{ ...btnBase, padding: '3px 10px', fontSize: 11, background: '#1d4ed8', color: '#fff' }}>
              Copy
            </button>
          </div>
          <pre style={{
            margin: 0, fontSize: 11, color: '#86efac', fontFamily: 'monospace',
            maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap',
          }}>
            {exportJSON()}
          </pre>
        </div>
      )}

      {/* ── Main area ── */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

        {/* ── Left: scrollable canvas ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>

          {/* Placement banner — shown while placing a required part */}
          {placingRequiredKey && (
            <div style={{
              background: '#fef3c7', borderBottom: '2px solid #fcd34d',
              padding: '8px 18px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
            }}>
              <span style={{ fontSize: 16 }}>📍</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
                Click on image to place:&nbsp;<em>{placingRequiredKey.label}</em>
              </span>
              <span style={{ fontSize: 11, color: '#a16207' }}>
                ({placingRequiredKey.key} · {placingRequiredKey.view} view)
              </span>
              <button
                onClick={() => setPlacingRequiredKey(null)}
                style={{ marginLeft: 'auto', ...btnBase, background: '#fde68a', color: '#78350f', fontSize: 12 }}
              >
                Cancel
              </button>
            </div>
          )}

        {/* ── Space-flip animation overlay ── */}
        {flipPhase && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 200, pointerEvents: 'none',
            animation: `bpSpaceAura 1.3s ease-in-out forwards`,
            background: `
              radial-gradient(ellipse at 50% 50%, rgba(120,0,255,0.85) 0%, rgba(20,0,80,0.97) 45%, rgba(0,0,15,1) 100%),
              radial-gradient(ellipse at 20% 30%, rgba(0,100,255,0.4) 0%, transparent 60%),
              radial-gradient(ellipse at 80% 70%, rgba(180,0,255,0.3) 0%, transparent 60%)
            `,
            overflow: 'hidden',
          }}>
            {/* Warp-speed star streaks */}
            {[...Array(24)].map((_, i) => (
              <div key={i} style={{
                position: 'absolute',
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${60 + Math.random() * 120}px`,
                height: '1.5px',
                background: `rgba(${200 + Math.floor(Math.random()*55)},${180 + Math.floor(Math.random()*75)},255,${0.4 + Math.random()*0.5})`,
                transform: `rotate(${-10 + Math.random() * 20}deg)`,
                animation: `bpWarpStreak ${0.4 + Math.random() * 0.5}s linear ${Math.random() * 0.3}s forwards`,
                borderRadius: 2,
                boxShadow: `0 0 6px rgba(160,120,255,0.8)`,
              }} />
            ))}
            {/* Energy rings */}
            {[0.7, 1.0, 1.4].map((scale, i) => (
              <div key={i} style={{
                position: 'absolute',
                top: '50%', left: '50%',
                width: `${120 * scale}px`, height: `${120 * scale}px`,
                transform: 'translate(-50%,-50%)',
                borderRadius: '50%',
                border: `${3 - i}px solid rgba(160,80,255,${0.8 - i * 0.2})`,
                boxShadow: `0 0 ${20 + i * 10}px rgba(120,0,255,${0.6 - i * 0.15}), inset 0 0 ${15 + i * 8}px rgba(100,0,255,${0.3 - i * 0.07})`,
                animation: `bpEnergyRing 1.3s ease-out ${i * 0.1}s forwards`,
              }} />
            ))}
            {/* Centre orb */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              width: 60, height: 60,
              transform: 'translate(-50%,-50%)',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,200,255,0.95) 0%, rgba(160,60,255,0.7) 50%, transparent 80%)',
              boxShadow: '0 0 40px 20px rgba(160,60,255,0.6), 0 0 80px 40px rgba(80,0,200,0.3)',
              animation: 'bpOrbPulse 1.3s ease-in-out forwards',
            }} />
            {/* View label */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, 60px)',
              color: 'rgba(220,200,255,0.9)', fontWeight: 800, fontSize: 18,
              letterSpacing: 4, textTransform: 'uppercase',
              textShadow: '0 0 20px rgba(160,80,255,1)',
              animation: 'bpOrbPulse 1.3s ease-in-out forwards',
            }}>
              ✦ {view === 'front' ? 'Back' : 'Front'} View ✦
            </div>
          </div>
        )}

        <div
          ref={scrollRef}
          style={{
            flex: 1, overflow: 'auto', background: '#cbd5e1', padding: 24,
            animation: flipPhase === 'exit' ? 'bpFlipExit 0.65s cubic-bezier(0.4,0,1,1) forwards'
                     : flipPhase === 'enter' ? 'bpFlipEnter 0.65s cubic-bezier(0,0,0.6,1) forwards'
                     : 'none',
            transformOrigin: 'center center',
          }}
        >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${SVG_W} ${SVG_H}`}
              style={{
                display: 'block',
                width:  svgPxW,
                height: svgPxH,
                cursor: pending ? 'default' : 'crosshair',
                touchAction: 'none',
                userSelect: 'none',
                boxShadow: '0 6px 32px rgba(0,0,0,0.25)',
                borderRadius: 6,
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            >
              {/* Body image */}
              <image
                href={`/${view}_body.png?v=2`}
                x={0} y={0}
                width={SVG_W} height={SVG_H}
                preserveAspectRatio="none"
              />

              {/* ── Saved annotations ── */}
              {current.map(a => {
                const isHovered = hoveredId === a.id;
                const isInfoSelected = selectedInfoPart?.id === a.id;
                return (
                  <g
                    key={a.id}
                    style={{ cursor: 'pointer' }}
                    onPointerDown={e => e.stopPropagation()}
                    onClick={e => { e.stopPropagation(); setSelectedInfoPart(isInfoSelected ? null : a); }}
                    onDoubleClick={e => { e.stopPropagation(); startEdit(a.id, a.label); }}
                    onMouseEnter={() => setHoveredId(a.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    {a.type === 'line' ? (
                      <LineAnnot a={a} showLabel={isHovered || isInfoSelected} selected={isInfoSelected} />
                    ) : (
                      <>
                        <circle cx={a.cx} cy={a.cy} r={a.r}
                          fill={isInfoSelected ? 'rgba(79,70,229,0.55)' : 'rgba(239,68,68,0.42)'}
                          stroke={isInfoSelected ? '#4f46e5' : '#dc2626'}
                          strokeWidth={isInfoSelected ? 2.5 : 1.5}
                        />
                        {(isHovered || isInfoSelected) && (
                          <text x={a.cx} y={a.cy + a.r + 10}
                            textAnchor="middle" fontSize="9" fontWeight="700"
                            fill={isInfoSelected ? '#4f46e5' : '#1e293b'} stroke="#fff" strokeWidth="3" paintOrder="stroke"
                            style={{ pointerEvents: 'none' }}>
                            {a.label}
                          </text>
                        )}
                      </>
                    )}
                  </g>
                );
              })}

              {/* ── Drag preview ── */}
              {previewEl}

              {/* ── Pending annotation preview ── */}
              {pending && (
                pending.type === 'line'
                  ? <PendingLinePreview p={pending} lineStyle={pendingLineStyle} />
                  : <circle
                      cx={pending.cx} cy={pending.cy} r={pending.r}
                      fill="rgba(245,158,11,0.45)" stroke="#d97706" strokeWidth="2"
                      style={{ pointerEvents: 'none' }}
                    />
              )}
            </svg>

            {/* ── Floating label + line-style input ── */}
            {pending && (
              <div style={{
                position: 'absolute',
                left: popupLeft,
                top:  popupTop,
                background: '#fff',
                border: '2px solid #f59e0b',
                borderRadius: 10,
                padding: '9px 11px',
                boxShadow: '0 6px 22px rgba(0,0,0,0.22)',
                display: 'flex', flexDirection: 'column', gap: 8,
                zIndex: 30,
                minWidth: 210,
              }}>
                {/* Label input */}
                <input
                  ref={inputRef}
                  value={pendingLabel}
                  onChange={e => setPendingLabel(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') confirmPending();
                    if (e.key === 'Escape') cancelPending();
                  }}
                  placeholder="Body part name…"
                  style={{
                    border: 'none', outline: 'none', fontSize: 13,
                    fontWeight: 600, color: '#1e293b', width: '100%',
                    borderBottom: '1px solid #e5e7eb', paddingBottom: 6,
                  }}
                />

                {/* Sequence category (stype) picker */}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, marginBottom: 3 }}>
                    SEQUENCE CATEGORY (for CIV/GTP)
                  </div>
                  <input
                    list="stype-options"
                    value={pendingStype}
                    onChange={e => setPendingStype(e.target.value)}
                    placeholder="e.g. Shoulder, Knee… (optional)"
                    style={{
                      width: '100%', fontSize: 12, padding: '5px 8px',
                      border: '1.5px solid #e5e7eb', borderRadius: 6,
                      color: pendingStype ? '#1e293b' : '#94a3b8',
                      outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                  <datalist id="stype-options">
                    {STYPE_OPTIONS.map(s => <option key={s} value={s} />)}
                  </datalist>
                  {!pendingStype && (
                    <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 2 }}>
                      ⚠ Without a category, sequences won't load for this part in CIV/GTP
                    </div>
                  )}
                </div>

                {/* Line style picker — shown only for drag (line) annotations */}
                {pending.type === 'line' && (
                  <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                    {[
                      ['single', '─', 'Single line'],
                      ['triple', '≡', 'Triple line'],
                    ].map(([val, icon, lbl]) => (
                      <label key={val} style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        cursor: 'pointer', userSelect: 'none',
                        fontWeight: pendingLineStyle === val ? 700 : 400,
                        color:      pendingLineStyle === val ? '#d97706' : '#64748b',
                      }}>
                        <input
                          type="radio"
                          name="bpls"
                          value={val}
                          checked={pendingLineStyle === val}
                          onChange={() => setPendingLineStyle(val)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: 15, lineHeight: 1 }}>{icon}</span>
                        {lbl}
                      </label>
                    ))}
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={confirmPending} style={{
                    ...btnBase, flex: 1,
                    background: '#22c55e', color: '#fff', fontSize: 13,
                  }}>✓ Add</button>
                  <button onClick={cancelPending} style={{
                    ...btnBase, background: '#f1f5f9', color: '#64748b', fontSize: 13,
                  }}>✕</button>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>{/* end left flex-column */}

        {/* ── Right: annotation list panel ── */}
        <div style={{
          width: 310, background: '#fff',
          borderLeft: '1px solid #e2e8f0',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden', flexShrink: 0,
        }}>
          {/* ── Info Box — shown when a body part is clicked on the SVG ── */}
          {selectedInfoPart && (() => {
            const a = selectedInfoPart;
            const reqPart = REQUIRED_PARTS.find(r => r.key === a.key);
            const links = a.romLinkedParts || [];
            const mt = a.metricType || '';
            return (
              <div style={{
                background: '#f0f4ff', borderBottom: '2px solid #818cf8',
                padding: '10px 14px', flexShrink: 0, position: 'relative',
              }}>
                {/* Close button */}
                <button onClick={() => setSelectedInfoPart(null)} style={{
                  position: 'absolute', top: 6, right: 8,
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 16, color: '#64748b', fontWeight: 700, lineHeight: 1,
                }}>✕</button>

                {/* Header */}
                <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b', marginBottom: 6, paddingRight: 20 }}>
                  {a.label}
                </div>

                {/* Details grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px', fontSize: 11 }}>
                  <span style={{ color: '#6b7280', fontWeight: 600 }}>Key:</span>
                  <span style={{ fontFamily: 'monospace', color: '#1e293b' }}>{a.key}</span>

                  <span style={{ color: '#6b7280', fontWeight: 600 }}>Type:</span>
                  <span style={{ color: '#1e293b' }}>
                    {a.type === 'line' ? `Line (${a.lineStyle || 'single'})` : 'Dot'}
                  </span>

                  <span style={{ color: '#6b7280', fontWeight: 600 }}>Position:</span>
                  <span style={{ fontFamily: 'monospace', color: '#1e293b' }}>
                    {a.type === 'dot'
                      ? `cx:${a.cx} cy:${a.cy} r:${a.r}`
                      : `(${a.x1},${a.y1}) → (${a.x2},${a.y2})`}
                  </span>

                  <span style={{ color: '#6b7280', fontWeight: 600 }}>View:</span>
                  <span style={{ color: '#1e293b', textTransform: 'capitalize' }}>{view}</span>

                  <span style={{ color: '#6b7280', fontWeight: 600 }}>Stype:</span>
                  <span style={{ color: a.stype ? '#1e293b' : '#9ca3af' }}>
                    {a.stype || '(none)'}
                  </span>

                  <span style={{ color: '#6b7280', fontWeight: 600 }}>Metric:</span>
                  <span style={{
                    fontWeight: 700,
                    color: mt === 'both' ? '#b45309' : mt === 'ROM' ? '#1d4ed8' : mt === 'MVC' ? '#7c3aed' : '#9ca3af',
                  }}>
                    {mt || '(none)'}
                  </span>

                  {a.romKey && (
                    <>
                      <span style={{ color: '#6b7280', fontWeight: 600 }}>ROM Joint:</span>
                      <span style={{ color: '#1e293b', fontWeight: 600 }}>
                        {clinicalConfig?.romParts?.find(r => r.key === a.romKey)?.label || a.romKey}
                      </span>
                    </>
                  )}

                  {(a.muscles || []).length > 0 && (
                    <>
                      <span style={{ color: '#6b7280', fontWeight: 600 }}>Muscles:</span>
                      <span style={{ color: '#1e293b' }}>
                        {a.muscles.map(mk => clinicalConfig?.muscleParts?.find(m => m.key === mk)?.label || mk).join(', ')}
                      </span>
                    </>
                  )}

                  {links.length > 0 && (
                    <>
                      <span style={{ color: '#6b7280', fontWeight: 600 }}>ROM Links:</span>
                      <span style={{ color: '#1e293b' }}>
                        {links.map(k => allConfiguredParts.find(p => p.key === k)?.label || k).join(', ')}
                      </span>
                    </>
                  )}

                  {reqPart && (
                    <>
                      <span style={{ color: '#6b7280', fontWeight: 600 }}>Required:</span>
                      <span style={{ color: '#16a34a', fontWeight: 600 }}>Yes ({reqPart.view} view)</span>
                    </>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Sub-tab bar */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', flexShrink: 0 }}>
            {[
              { id: 'custom',   label: `Custom (${current.length})` },
              { id: 'required', label: `Required (${configuredCount}/${REQUIRED_PARTS.length} ✓)` },
            ].map(tab => (
              <button key={tab.id} onClick={() => setRightTab(tab.id)} style={{
                flex: 1, padding: '9px 6px', border: 'none', cursor: 'pointer',
                fontWeight: 700, fontSize: 11,
                background: rightTab === tab.id ? '#fff' : '#f8fafc',
                color:      rightTab === tab.id ? '#1e293b' : '#94a3b8',
                borderBottom: rightTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                marginBottom: -2,
              }}>{tab.label}</button>
            ))}
          </div>

          {/* ── Custom tab ── */}
          {rightTab === 'custom' && (() => {
            // Detect duplicate keys within this view
            const keyCounts = {};
            current.forEach(a => { keyCounts[a.key] = (keyCounts[a.key] || 0) + 1; });
            // Also check cross-view duplicates (same key in front AND back)
            const otherView = view === 'front' ? annotations.back : annotations.front;
            const otherKeys = new Set(otherView.map(a => a.key));
            const dupKeys = new Set(Object.entries(keyCounts).filter(([, c]) => c > 1).map(([k]) => k));
            const crossDupKeys = new Set(current.filter(a => otherKeys.has(a.key)).map(a => a.key));
            const totalDups = current.filter(a => dupKeys.has(a.key)).length - dupKeys.size; // extra copies
            const totalCrossDups = crossDupKeys.size;
            return (
            <>
              <div style={{ padding: '8px 14px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                  {view === 'front' ? '🫁 Front' : '🫀 Back'} · Double-click canvas or ✎ to rename
                </div>
                {(totalDups > 0 || totalCrossDups > 0) && (
                  <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 700, marginTop: 3 }}>
                    {totalDups > 0 && <span>⚠ {totalDups} duplicate{totalDups !== 1 ? 's' : ''} in this view</span>}
                    {totalDups > 0 && totalCrossDups > 0 && <span> · </span>}
                    {totalCrossDups > 0 && <span>{totalCrossDups} key{totalCrossDups !== 1 ? 's' : ''} also in {view === 'front' ? 'back' : 'front'} view</span>}
                  </div>
                )}
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {current.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '32px 16px' }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>👆</div>
                    Click (dot) or drag (line) on the image
                  </div>
                ) : (
                  current.map(a => {
                    const cMType = a.metricType || '';
                    const cLinks = a.romLinkedParts || [];
                    const cIsLinkEdit = linkEditKey === a.key;
                    const isDup = dupKeys.has(a.key);
                    const isCrossDup = crossDupKeys.has(a.key);
                    return (
                    <div key={a.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '7px 12px',
                        background: editId === a.id ? '#fef9c3' : isDup ? '#fef2f2' : isCrossDup ? '#fffbeb' : 'transparent',
                      }}>
                        <div style={{ flexShrink: 0, fontSize: 16, width: 20, textAlign: 'center', color: '#dc2626', fontWeight: 900 }}>
                          {a.type === 'line' ? (a.lineStyle === 'triple' ? '≡' : '─') : '●'}
                        </div>
                        {editId === a.id ? (
                          <input
                            ref={editInputRef}
                            value={editLabel}
                            onChange={e => setEditLabel(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') confirmEdit();
                              if (e.key === 'Escape') setEditId(null);
                            }}
                            onBlur={confirmEdit}
                            style={{ flex: 1, border: '1.5px solid #f59e0b', borderRadius: 5, padding: '3px 7px', fontSize: 12, fontWeight: 600, outline: 'none' }}
                          />
                        ) : (
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                              {a.label}
                              {isDup && (
                                <span style={{ fontSize: 8, fontWeight: 800, background: '#dc2626', color: '#fff', borderRadius: 4, padding: '1px 4px', flexShrink: 0 }}>
                                  DUP ×{keyCounts[a.key]}
                                </span>
                              )}
                              {isCrossDup && !isDup && (
                                <span style={{ fontSize: 8, fontWeight: 800, background: '#d97706', color: '#fff', borderRadius: 4, padding: '1px 4px', flexShrink: 0 }}>
                                  CROSS
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 10, color: isDup ? '#dc2626' : '#94a3b8', fontFamily: 'monospace' }}>
                              {a.key}&nbsp;·&nbsp;
                              {a.type === 'dot' ? `(${a.cx},${a.cy})` : `(${a.x1},${a.y1})→(${a.x2},${a.y2})`}
                            </div>
                          </div>
                        )}

                        {/* Metric type toggle (MVC / ROM / None) */}
                        <button onClick={() => toggleMetricType(a.key)} title="Cycle: None → MVC → ROM → Both" style={{
                          ...btnBase, flexShrink: 0, fontSize: 9, padding: '2px 6px', fontWeight: 700,
                          background: cMType === 'both' ? '#fef3c7' : cMType === 'ROM' ? '#dbeafe' : cMType === 'MVC' ? '#f3e8ff' : '#f1f5f9',
                          color:      cMType === 'both' ? '#b45309' : cMType === 'ROM' ? '#1d4ed8' : cMType === 'MVC' ? '#7c3aed' : '#94a3b8',
                          border: cMType === 'both' ? '1px solid #fbbf24' : cMType === 'ROM' ? '1px solid #93c5fd' : cMType === 'MVC' ? '1px solid #c4b5fd' : '1px solid #e2e8f0',
                        }}>
                          {cMType || '—'}
                        </button>

                        {/* ROM linkage button */}
                        {(cMType === 'ROM' || cMType === 'both') && (
                          <button
                            onClick={() => setLinkEditKey(cIsLinkEdit ? null : a.key)}
                            title="Edit ROM linkages"
                            style={{
                              ...btnBase, flexShrink: 0, fontSize: 9, padding: '2px 5px',
                              background: cIsLinkEdit ? '#fef3c7' : '#f0f9ff',
                              color: '#0369a1', border: '1px solid #bae6fd',
                            }}
                          >
                            {cLinks.length > 0 ? `${cLinks.length}` : '+'}
                          </button>
                        )}

                        {/* Clinical mapping editor button */}
                        {clinicalConfig && (
                          <button onClick={() => setClinicalEditKey(clinicalEditKey === a.key ? null : a.key)}
                            title="Edit ROM joint & muscles mapping"
                            style={{
                              ...btnBase, flexShrink: 0, fontSize: 9, padding: '2px 5px',
                              background: clinicalEditKey === a.key ? '#fef3c7' : '#f0fdf4',
                              color: '#15803d', border: '1px solid #bbf7d0',
                            }}>
                            {a.romKey || (a.muscles || []).length > 0 ? '⚙' : '⚙+'}
                          </button>
                        )}

                        <button onClick={() => startEdit(a.id, a.label)} title="Rename"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6b7280', padding: '2px 3px' }}>✎</button>
                        <button onClick={() => deleteAnnotation(a.id)} title="Delete"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#dc2626', padding: '2px 3px' }}>🗑</button>
                      </div>

                      {/* Clinical mapping editor (romKey + muscles) */}
                      {clinicalEditKey === a.key && clinicalConfig && (
                        <div style={{
                          padding: '6px 10px 8px 36px', background: '#f0fdf4',
                          borderTop: '1px solid #bbf7d0',
                        }}>
                          <div style={{ fontSize: 10, color: '#15803d', fontWeight: 700, marginBottom: 4 }}>
                            Clinical Mappings:
                          </div>
                          {/* ROM Joint dropdown */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, minWidth: 60 }}>ROM Joint:</span>
                            <select
                              value={a.romKey || ''}
                              onChange={e => updateAnnotationField(a.key, 'romKey', e.target.value)}
                              style={{ flex: 1, fontSize: 10, padding: '2px 4px', borderRadius: 4, border: '1px solid #d1d5db' }}
                            >
                              <option value="">(none)</option>
                              {(clinicalConfig.romParts || []).map(rp => (
                                <option key={rp.key} value={rp.key}>{rp.label}</option>
                              ))}
                            </select>
                          </div>
                          {/* Muscles multi-select */}
                          <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>Muscles:</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {(clinicalConfig.muscleParts || []).map(mp => {
                              const hasMuscle = (a.muscles || []).includes(mp.key);
                              return (
                                <button key={mp.key} onClick={() => {
                                  const cur = a.muscles || [];
                                  const next = hasMuscle ? cur.filter(k => k !== mp.key) : [...cur, mp.key];
                                  updateAnnotationField(a.key, 'muscles', next);
                                }} style={{
                                  ...btnBase, fontSize: 8, padding: '1px 5px',
                                  background: hasMuscle ? '#15803d' : '#e2e8f0',
                                  color: hasMuscle ? '#fff' : '#64748b',
                                  border: 'none',
                                }}>
                                  {mp.label}
                                </button>
                              );
                            })}
                          </div>
                          {(a.muscles || []).length > 0 && (
                            <div style={{ fontSize: 9, color: '#64748b', marginTop: 4 }}>
                              {a.muscles.length} muscle{a.muscles.length !== 1 ? 's' : ''}: {a.muscles.map(mk => clinicalConfig.muscleParts?.find(m => m.key === mk)?.label || mk).join(', ')}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ROM linkage editor (inline expandable) */}
                      {cIsLinkEdit && (
                        <div style={{
                          padding: '6px 10px 8px 36px', background: '#f0f9ff',
                          borderTop: '1px solid #bae6fd',
                        }}>
                          <div style={{ fontSize: 10, color: '#0369a1', fontWeight: 700, marginBottom: 4 }}>
                            Linked ROM parts:
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {allConfiguredParts.filter(p => p.key !== a.key).map(p => {
                              const isLinked = cLinks.includes(p.key);
                              return (
                                <button key={p.key} onClick={() => toggleRomLink(a.key, p.key)} style={{
                                  ...btnBase, fontSize: 9, padding: '1px 5px',
                                  background: isLinked ? '#1d4ed8' : '#e2e8f0',
                                  color: isLinked ? '#fff' : '#64748b',
                                  border: 'none',
                                }}>
                                  {p.label}
                                </button>
                              );
                            })}
                          </div>
                          {cLinks.length > 0 && (
                            <div style={{ fontSize: 9, color: '#64748b', marginTop: 4 }}>
                              {cLinks.length} linked: {cLinks.map(k => allConfiguredParts.find(p => p.key === k)?.label || k).join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );})

                )}
              </div>
            </>
          );})()}

          {/* ── Required tab ── */}
          {rightTab === 'required' && (
            <>
              <div style={{ padding: '7px 12px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0, fontSize: 11, color: '#64748b' }}>
                Click <strong>↗ Place</strong> beside any part, then click on the image to set its position.
                Already-configured parts show <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>.
                Toggle <strong>MVC/ROM</strong> to set metric type.
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {REQUIRED_PARTS.map(part => {
                  const configured = isConfigured(part.key);
                  const isActive   = placingRequiredKey?.key === part.key;
                  const ann        = getAnnotation(part.key);
                  const mType      = ann?.metricType || '';
                  const links      = ann?.romLinkedParts || [];
                  const isLinkEdit = linkEditKey === part.key;
                  return (
                    <div key={part.key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '5px 10px',
                        background: isActive ? '#fef3c7' : configured ? '#f0fdf4' : '#fff',
                      }}>
                        {/* Status */}
                        <span style={{ flexShrink: 0, fontSize: 13, width: 16, textAlign: 'center', fontWeight: 900,
                          color: configured ? '#16a34a' : '#d1d5db' }}>
                          {configured ? '✓' : '○'}
                        </span>

                        {/* Label + key */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: configured ? 600 : 400,
                            color: configured ? '#15803d' : '#374151',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {part.label}
                          </div>
                          <div style={{ fontSize: 9, color: '#94a3b8', fontFamily: 'monospace' }}>
                            {part.key}
                          </div>
                        </div>

                        {/* Metric type toggle (MVC / ROM / Both / None) */}
                        {configured && (
                          <button onClick={() => toggleMetricType(part.key)} title="Cycle: None → MVC → ROM → Both" style={{
                            ...btnBase, flexShrink: 0, fontSize: 9, padding: '2px 6px', fontWeight: 700,
                            background: mType === 'both' ? '#fef3c7' : mType === 'ROM' ? '#dbeafe' : mType === 'MVC' ? '#f3e8ff' : '#f1f5f9',
                            color:      mType === 'both' ? '#b45309' : mType === 'ROM' ? '#1d4ed8' : mType === 'MVC' ? '#7c3aed' : '#94a3b8',
                            border: mType === 'both' ? '1px solid #fbbf24' : mType === 'ROM' ? '1px solid #93c5fd' : mType === 'MVC' ? '1px solid #c4b5fd' : '1px solid #e2e8f0',
                          }}>
                            {mType || '—'}
                          </button>
                        )}

                        {/* ROM linkage button (for ROM or both parts) */}
                        {configured && (mType === 'ROM' || mType === 'both') && (
                          <button
                            onClick={() => setLinkEditKey(isLinkEdit ? null : part.key)}
                            title="Edit ROM linkages"
                            style={{
                              ...btnBase, flexShrink: 0, fontSize: 9, padding: '2px 5px',
                              background: isLinkEdit ? '#fef3c7' : '#f0f9ff',
                              color: '#0369a1', border: '1px solid #bae6fd',
                            }}
                          >
                            {links.length > 0 ? `${links.length}` : '+'}
                          </button>
                        )}

                        {/* Clinical mapping editor button */}
                        {configured && clinicalConfig && (
                          <button onClick={() => setClinicalEditKey(clinicalEditKey === part.key ? null : part.key)}
                            title="Edit ROM joint & muscles mapping"
                            style={{
                              ...btnBase, flexShrink: 0, fontSize: 9, padding: '2px 5px',
                              background: clinicalEditKey === part.key ? '#fef3c7' : '#f0fdf4',
                              color: '#15803d', border: '1px solid #bbf7d0',
                            }}>
                            {ann?.romKey || (ann?.muscles || []).length > 0 ? '⚙' : '⚙+'}
                          </button>
                        )}

                        {/* View badge */}
                        <span style={{
                          flexShrink: 0, fontSize: 9, padding: '1px 5px', borderRadius: 8, fontWeight: 700,
                          background: part.view === 'front' ? '#dbeafe' : '#ede9fe',
                          color:      part.view === 'front' ? '#1d4ed8' : '#6d28d9',
                        }}>
                          {part.view}
                        </span>

                        {/* Place / cancel button */}
                        {isActive ? (
                          <button onClick={() => setPlacingRequiredKey(null)} style={{
                            ...btnBase, flexShrink: 0, fontSize: 10, padding: '3px 7px',
                            background: '#dc2626', color: '#fff',
                          }}>✕</button>
                        ) : (
                          <button onClick={() => {
                            setPlacingRequiredKey(part);
                            if (part.view !== view) { setPending(null); setView(part.view); }
                          }} style={{
                            ...btnBase, flexShrink: 0, fontSize: 10, padding: '3px 7px',
                            background: configured ? '#e5e7eb' : '#1e293b',
                            color:      configured ? '#6b7280' : '#fff',
                          }}>
                            {configured ? '↺' : '↗'}
                          </button>
                        )}
                      </div>

                      {/* ROM linkage editor (inline expandable) */}
                      {isLinkEdit && (
                        <div style={{
                          padding: '6px 10px 8px 36px', background: '#f0f9ff',
                          borderTop: '1px solid #bae6fd',
                        }}>
                          <div style={{ fontSize: 10, color: '#0369a1', fontWeight: 700, marginBottom: 4 }}>
                            Linked ROM parts:
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {allConfiguredParts.filter(p => p.key !== part.key).map(p => {
                              const isLinked = links.includes(p.key);
                              return (
                                <button key={p.key} onClick={() => toggleRomLink(part.key, p.key)} style={{
                                  ...btnBase, fontSize: 9, padding: '1px 5px',
                                  background: isLinked ? '#1d4ed8' : '#e2e8f0',
                                  color: isLinked ? '#fff' : '#64748b',
                                  border: 'none',
                                }}>
                                  {p.label}
                                </button>
                              );
                            })}
                          </div>
                          {links.length > 0 && (
                            <div style={{ fontSize: 9, color: '#64748b', marginTop: 4 }}>
                              {links.length} linked: {links.map(k => allConfiguredParts.find(p => p.key === k)?.label || k).join(', ')}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Clinical mapping editor (romKey + muscles) */}
                      {clinicalEditKey === part.key && clinicalConfig && ann && (
                        <div style={{
                          padding: '6px 10px 8px 36px', background: '#f0fdf4',
                          borderTop: '1px solid #bbf7d0',
                        }}>
                          <div style={{ fontSize: 10, color: '#15803d', fontWeight: 700, marginBottom: 4 }}>
                            Clinical Mappings:
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, minWidth: 60 }}>ROM Joint:</span>
                            <select
                              value={ann.romKey || ''}
                              onChange={e => updateAnnotationField(part.key, 'romKey', e.target.value)}
                              style={{ flex: 1, fontSize: 10, padding: '2px 4px', borderRadius: 4, border: '1px solid #d1d5db' }}
                            >
                              <option value="">(none)</option>
                              {(clinicalConfig.romParts || []).map(rp => (
                                <option key={rp.key} value={rp.key}>{rp.label}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>Muscles:</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {(clinicalConfig.muscleParts || []).map(mp => {
                              const hasMuscle = (ann.muscles || []).includes(mp.key);
                              return (
                                <button key={mp.key} onClick={() => {
                                  const cur = ann.muscles || [];
                                  const next = hasMuscle ? cur.filter(k => k !== mp.key) : [...cur, mp.key];
                                  updateAnnotationField(part.key, 'muscles', next);
                                }} style={{
                                  ...btnBase, fontSize: 8, padding: '1px 5px',
                                  background: hasMuscle ? '#15803d' : '#e2e8f0',
                                  color: hasMuscle ? '#fff' : '#64748b',
                                  border: 'none',
                                }}>
                                  {mp.label}
                                </button>
                              );
                            })}
                          </div>
                          {(ann.muscles || []).length > 0 && (
                            <div style={{ fontSize: 9, color: '#64748b', marginTop: 4 }}>
                              {ann.muscles.length} muscle{ann.muscles.length !== 1 ? 's' : ''}: {ann.muscles.map(mk => clinicalConfig.muscleParts?.find(m => m.key === mk)?.label || mk).join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Footer stats */}
          <div style={{
            padding: '7px 14px', borderTop: '1px solid #e2e8f0',
            background: '#f8fafc', fontSize: 11, color: '#64748b',
            display: 'flex', gap: 10, flexShrink: 0,
          }}>
            <span>F: <strong>{annotations.front.length}</strong></span>
            <span>B: <strong>{annotations.back.length}</strong></span>
            <span style={{ color: '#7c3aed' }}>MVC: <strong>{[...annotations.front, ...annotations.back].filter(a => a.metricType === 'MVC' || a.metricType === 'both').length}</strong></span>
            <span style={{ color: '#1d4ed8' }}>ROM: <strong>{[...annotations.front, ...annotations.back].filter(a => a.metricType === 'ROM' || a.metricType === 'both').length}</strong></span>
            <span style={{ color: '#b45309' }}>Both: <strong>{[...annotations.front, ...annotations.back].filter(a => a.metricType === 'both').length}</strong></span>
            <span style={{ marginLeft: 'auto', color: configuredCount === REQUIRED_PARTS.length ? '#16a34a' : '#64748b', fontWeight: 700 }}>
              Req: {configuredCount}/{REQUIRED_PARTS.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
